import { MenuItem, ListItemIcon } from '@mui/material';
import React from 'react';
import { useSnackbar } from '@/alerts/AlertProvider';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAccountLists } from '@/dashboard/AccountLists/useAccountLists/useAccountLists';
import { useAccountListsV2 } from '@/dashboard/AccountListsV2/hooks/useAccountListsV2';
import {
  LizanoListInfo,
  LizanoMetaData,
} from '@/reports/sections/ListsSection/lists/lists';
import { AddReportToListV2AddressPayload } from '@/reports/types/serialized';
import { Icons, Text } from '@/theme/index';
import { ValidReportTypes } from '@/utils/helperTypes';
import { titleize } from '@/utils/index';

interface ListMenuItemProps {
  reportData?: AddReportToListV2AddressPayload;
  reportType?: ValidReportTypes;
  reportPermaLink: string;
  list: LizanoListInfo;
}
export function ListMenuItem({
  reportData,
  reportType,
  reportPermaLink,
  list,
}: ListMenuItemProps) {
  const { trackEvent } = useAppConfigContext();
  const { addReportToList, removeReportFromList } = useAccountLists();
  const { addReportToListV2, bulkDeleteListProperty } = useAccountListsV2();
  const { enqueueSnackbar } = useSnackbar();

  const isPropertyReport = reportType && reportType === 'property';

  const reportFoundInList = list.list_reports.find(
    (lizanoReportObj: LizanoMetaData) =>
      lizanoReportObj?.report?.permalink === reportPermaLink
  );

  const reportFoundInListV2 =
    isPropertyReport &&
    list.list_properties?.find(
      (lizanoReportObj: LizanoMetaData) => lizanoReportObj?.list_id === list.id
    );

  const handleAddReportToListV2 = () => {
    if (!reportData) return;

    addReportToListV2.mutate({
      list_code: list.list_code,
      property: reportData,
    });
  };

  const handleAddReportToList = () => {
    trackEvent('Add to Existing List', 'engagement click', 'rats component');
    addReportToList.mutate(
      {
        list_code: list.list_code,
        reportPermaLink,
      },
      {
        onSuccess: (response) => {
          if (isPropertyReport) {
            handleAddReportToListV2();
          }
          const listName = response?.list?.name;
          enqueueSnackbar(`Added to ${titleize(listName)}`, {
            variant: 'success',
          });
        },
      }
    );
  };

  const handleRemoveReportFromListV2 = () => {
    if (!reportFoundInListV2) return;

    bulkDeleteListProperty.mutate({
      list_code: list.list_code,
      propertyIDs: [reportFoundInListV2?.id.toString()],
    });
  };

  const handleRemoveReportFromList = () => {
    if (!reportFoundInList) return;

    trackEvent(
      'Remove from Existing List',
      'engagement click',
      'rats component'
    );
    removeReportFromList.mutate(
      {
        list_code: list.list_code,
        listReportId: reportFoundInList.id,
      },
      {
        onSuccess: () => {
          if (isPropertyReport) {
            handleRemoveReportFromListV2();
          }
          enqueueSnackbar(`Removed from ${titleize(list.name)}`, {
            variant: 'success',
          });
        },
      }
    );
  };

  const handleAddRemoveAction = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    reportFoundInList ? handleRemoveReportFromList() : handleAddReportToList();
  };

  if (addReportToList.isLoading || removeReportFromList.isLoading) {
    return (
      <MenuItem>
        <ListItemIcon>
          <Icons.Sync fontSize="small" color="primary" />
        </ListItemIcon>
        <Text color="primary">Loading...</Text>
      </MenuItem>
    );
  }

  if (addReportToList.isError || removeReportFromList.isError) {
    return (
      <MenuItem>
        <ListItemIcon>
          <Icons.Error fontSize="small" color="error" />
        </ListItemIcon>
        <Text color="error">Error, updating List</Text>
      </MenuItem>
    );
  }

  return (
    <MenuItem onClick={handleAddRemoveAction}>
      <ListItemIcon>
        {reportFoundInList ? (
          <Icons.Check fontSize="small" color="primary" />
        ) : (
          <Icons.Add fontSize="small" color="primary" />
        )}
      </ListItemIcon>
      <Text color="primary">{titleize(list.name)}</Text>
    </MenuItem>
  );
}
